/**
 * Tour — visite guidée en plusieurs étapes, ancrée sur les éléments de la page.
 *
 *   Tour.start({ id, steps: [{ target?, title, text, placement?, padding?, before? }], onEnd? }, { force? })
 *   Tour.hasSeen(id) · Tour.reset(id) · Tour.end() · Tour.next() · Tour.prev()
 *
 * Un <dialog> ouvert en showModal() porte la découpe et la bulle : le reste de la page
 * est inerte, le focus piégé, Échap = « Passer », le focus revient à l'élément d'origine.
 * La visite est mémorisée (localStorage « ds-tour:<id> ») à la fin ou si on la passe.
 */

const STORAGE_PREFIX = 'ds-tour:';
const LABELS = {
  skip: 'Passer', prev: 'Précédent', next: 'Suivant', done: 'Terminer',
  dialog: 'Visite guidée', live: 'Étape {i} sur {n}',
};
const GAP = 12;
const MARGIN = 8;
let current = null;
let uid = 0;

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function hasSeen(id) {
  try { return !!localStorage.getItem(STORAGE_PREFIX + id); } catch (e) { return false; }
}

export function reset(id) {
  try { localStorage.removeItem(STORAGE_PREFIX + id); } catch (e) { /* stockage indisponible */ }
}

/** Démarre une visite. Renvoie false si elle a déjà été vue (sans force) ou si elle est vide. */
export function start(config, { force = false } = {}) {
  if (!config || !Array.isArray(config.steps) || !config.steps.length) return false;
  if (!force && config.id && hasSeen(config.id)) return false;
  if (current) current.finish(false);
  const tour = createTour(config);
  if (!tour) return false; // aucune étape affichable
  current = tour;
  current.go(0, 1);
  return true;
}

export const end = () => current?.finish(false);
export const next = () => current?.next();
export const prev = () => current?.prev();

export function initTour() {
  if (typeof window !== 'undefined') window.Tour = { start, end, next, prev, hasSeen, reset };
}

// ---------- Défilement du conteneur le plus proche (jamais scrollIntoView) ----------

function scrollParent(el) {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const { overflowY, overflowX } = getComputedStyle(p);
    if ((/(auto|scroll|overlay)/.test(overflowY) && p.scrollHeight > p.clientHeight)
      || (/(auto|scroll|overlay)/.test(overflowX) && p.scrollWidth > p.clientWidth)) return p;
  }
  return null;
}

function revealTarget(el, pad) {
  const behavior = reducedMotion() ? 'auto' : 'smooth';
  const r = el.getBoundingClientRect();
  const container = scrollParent(el);
  if (container) {
    const c = container.getBoundingClientRect();
    let dy = 0, dx = 0;
    if (r.top - pad < c.top) dy = r.top - pad - c.top - MARGIN;
    else if (r.bottom + pad > c.bottom) dy = Math.min(r.bottom + pad - c.bottom + MARGIN, r.top - pad - c.top - MARGIN);
    if (r.left - pad < c.left) dx = r.left - pad - c.left - MARGIN;
    else if (r.right + pad > c.right) dx = Math.min(r.right + pad - c.right + MARGIN, r.left - pad - c.left - MARGIN);
    if (dy || dx) container.scrollBy({ top: dy, left: dx, behavior });
    return;
  }
  // La page elle-même est le seul conteneur défilant : défilement minimal
  const vh = window.innerHeight;
  if (r.top - pad < 0) window.scrollBy({ top: r.top - pad - MARGIN, behavior });
  else if (r.bottom + pad > vh) window.scrollBy({ top: Math.min(r.bottom + pad - vh + MARGIN, r.top - pad - MARGIN), behavior });
}

// ---------- Visite ----------

function createTour(config) {
  const { id = null, onEnd } = config;
  // Étapes dont la cible est absente dès le départ (et sans before() qui pourrait la créer) :
  // retirées, pour que le compteur « 2 / 5 » reste juste
  const steps = config.steps.filter((step) => {
    if (!step.target || typeof step.before === 'function') return true;
    const el = typeof step.target === 'string' ? document.querySelector(step.target) : step.target;
    const ok = el instanceof Element && el.getClientRects().length > 0;
    if (!ok) console.warn(`[ds-tour] étape ignorée : cible introuvable ou masquée (${step.target})`);
    return ok;
  });
  if (!steps.length) return null;
  const labels = { ...LABELS, ...(config.labels || {}) };
  const n = ++uid;
  const returnFocus = document.activeElement;

  const dialog = document.createElement('dialog');
  dialog.className = 'tour';
  dialog.setAttribute('aria-labelledby', `tour-${n}-title`);
  dialog.setAttribute('aria-describedby', `tour-${n}-text`);
  dialog.innerHTML = `
    <div class="tour__spotlight" aria-hidden="true"></div>
    <div class="tour__bubble">
      <span class="tour__arrow" aria-hidden="true"></span>
      <p class="tour__counter" aria-hidden="true"></p>
      <h2 class="tour__title" id="tour-${n}-title"></h2>
      <p class="tour__text" id="tour-${n}-text"></p>
      <div class="tour__actions">
        <button type="button" class="btn btn-sm btn-link tour__skip"></button>
        <div class="tour__nav">
          <button type="button" class="btn btn-sm btn-secondary tour__prev"></button>
          <button type="button" class="btn btn-sm btn-primary tour__next"></button>
        </div>
      </div>
    </div>
    <div class="sr-only" aria-live="polite"></div>`;
  const $ = (sel) => dialog.querySelector(sel);
  const spotlight = $('.tour__spotlight');
  const bubble = $('.tour__bubble');
  const live = dialog.querySelector('[aria-live]');
  $('.tour__skip').textContent = labels.skip;
  $('.tour__prev').textContent = labels.prev;
  dialog.setAttribute('aria-roledescription', labels.dialog);
  document.body.append(dialog);
  dialog.showModal();

  let index = -1;
  let firstShown = null;
  const skipped = new Set(); // étapes sautées en cours de visite (cible absente après before()) // première étape réellement affichée (les étapes sautées ne comptent pas)
  let target = null;
  let pad = 6;
  let ended = false;
  let token = 0;
  let frame = 0;
  const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => schedule()) : null;

  function resolveTarget(step) {
    if (!step.target) return null;
    const el = typeof step.target === 'string' ? document.querySelector(step.target) : step.target;
    return el instanceof Element && el.getClientRects().length ? el : undefined; // undefined = introuvable
  }

  async function go(i, dir) {
    const my = ++token;
    while (i >= 0 && i < steps.length) {
      const step = steps[i];
      if (typeof step.before === 'function') {
        try { await step.before(); } catch (e) { console.warn('[ds-tour] before() a échoué', e); }
        if (my !== token || ended) return;
      }
      const el = resolveTarget(step);
      if (el === undefined) {
        console.warn(`[ds-tour] étape ${i + 1} ignorée : cible introuvable ou masquée (${step.target})`);
        skipped.add(i);
        i += dir;
        continue;
      }
      show(i, el);
      return;
    }
    // En reculant sans étape affichable avant : on reste sur l'étape courante
    if (dir < 0) return;
    finish(i >= steps.length);
  }

  function show(i, el) {
    const step = steps[i];
    index = i;
    resizeObserver?.disconnect();
    target = el;
    pad = step.padding ?? 6;
    dialog.classList.toggle('tour--centered', !el);

    // Compteur sur les seules étapes affichables
    const total = steps.length - skipped.size;
    const shownIndex = i - [...skipped].filter((k) => k < i).length;
    $('.tour__counter').textContent = `${shownIndex + 1} / ${total}`;
    $('.tour__title').textContent = step.title || '';
    $('.tour__text').textContent = step.text || '';
    if (firstShown === null || i < firstShown) firstShown = i;
    // Désactivé (et non masqué) à la première étape : les autres boutons ne bougent pas
    $('.tour__prev').disabled = i <= firstShown;
    const last = i === steps.length - 1;
    $('.tour__next').textContent = last ? labels.done : labels.next;
    live.textContent = `${labels.live.replace('{i}', shownIndex + 1).replace('{n}', total)} : ${step.title || ''}. ${step.text || ''}`;

    if (el) {
      revealTarget(el, pad);
      resizeObserver?.observe(el);
    }
    position();
    $('.tour__next').focus();
    document.dispatchEvent(new CustomEvent('ds-tour:step', { detail: { id, index: shownIndex, total } }));
  }

  function position() {
    frame = 0;
    if (!target) {
      bubble.className = 'tour__bubble';
      return;
    }
    const r = target.getBoundingClientRect();
    const radius = parseFloat(getComputedStyle(target).borderTopLeftRadius) || 0;
    Object.assign(spotlight.style, {
      top: `${r.top - pad}px`,
      left: `${r.left - pad}px`,
      width: `${r.width + pad * 2}px`,
      height: `${r.height + pad * 2}px`,
      borderRadius: radius ? `${radius + pad}px` : '',
    });

    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const bw = bubble.offsetWidth;
    const bh = bubble.offsetHeight;
    const box = { top: r.top - pad, bottom: r.bottom + pad, left: r.left - pad, right: r.right + pad };
    const space = { top: box.top, bottom: vh - box.bottom, left: box.left, right: vw - box.right };
    const fits = { top: space.top >= bh + GAP, bottom: space.bottom >= bh + GAP, left: space.left >= bw + GAP, right: space.right >= bw + GAP };
    const opposite = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
    const wanted = steps[index].placement || 'auto';
    let side;
    if (wanted !== 'auto' && fits[wanted]) side = wanted;
    else if (wanted !== 'auto' && fits[opposite[wanted]]) side = opposite[wanted];
    else side = ['bottom', 'top', 'right', 'left'].filter((s) => fits[s]).sort((a, b) => space[b] - space[a])[0]
      || Object.keys(space).sort((a, b) => space[b] - space[a])[0];

    let top;
    let left;
    if (side === 'bottom' || side === 'top') {
      top = side === 'bottom' ? box.bottom + GAP : box.top - GAP - bh;
      left = r.left + r.width / 2 - bw / 2;
    } else {
      left = side === 'right' ? box.right + GAP : box.left - GAP - bw;
      top = r.top + r.height / 2 - bh / 2;
    }
    left = Math.max(MARGIN, Math.min(left, vw - bw - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - bh - MARGIN));
    bubble.className = `tour__bubble tour__bubble--${side}`;
    bubble.style.top = `${Math.round(top)}px`;
    bubble.style.left = `${Math.round(left)}px`;
    // La flèche vise le centre de la cible, sans sortir de la bulle
    const arrow = side === 'bottom' || side === 'top'
      ? Math.max(16, Math.min(r.left + r.width / 2 - left, bw - 16))
      : Math.max(16, Math.min(r.top + r.height / 2 - top, bh - 16));
    bubble.style.setProperty('--tour-arrow', `${Math.round(arrow)}px`);
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(position);
  }

  function finish(completed) {
    if (ended) return;
    ended = true;
    token++;
    if (id) {
      try { localStorage.setItem(STORAGE_PREFIX + id, completed ? 'done' : 'skipped'); } catch (e) { /* indisponible */ }
    }
    removeEventListener('resize', schedule);
    removeEventListener('scroll', schedule, true);
    resizeObserver?.disconnect();
    if (frame) cancelAnimationFrame(frame);
    if (dialog.open) dialog.close();
    dialog.remove();
    if (returnFocus && typeof returnFocus.focus === 'function' && returnFocus.isConnected) returnFocus.focus();
    if (current === api) current = null;
    const detail = { id, completed, index };
    document.dispatchEvent(new CustomEvent('ds-tour:end', { detail }));
    if (typeof onEnd === 'function') onEnd(detail);
  }

  const api = {
    go,
    finish,
    next: () => (index >= steps.length - 1 ? finish(true) : go(index + 1, 1)),
    prev: () => (firstShown !== null && index > firstShown ? go(index - 1, -1) : undefined),
  };

  // ---------- Interactions ----------
  $('.tour__next').addEventListener('click', () => api.next());
  $('.tour__prev').addEventListener('click', () => api.prev());
  $('.tour__skip').addEventListener('click', () => finish(false));
  dialog.addEventListener('cancel', (e) => { e.preventDefault(); finish(false); }); // Échap
  dialog.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); api.next(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); api.prev(); }
  });
  addEventListener('resize', schedule);
  addEventListener('scroll', schedule, true);

  return api;
}
